import React, { useEffect, useState } from 'react';
import { Clock, CheckCircle2, PlayCircle, BookOpen } from 'lucide-react';
import { motion } from 'framer-motion';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useTranslation } from 'react-i18next';
import { formatDistanceToNow } from 'date-fns';

const UNI_GOLD = '#d4a843';
const UNI_BORDER = 'rgba(255,255,255,0.07)';
const UNI_CARD = 'rgba(255,255,255,0.04)';
const UNI_TEXT = 'rgba(255,255,255,0.88)';
const UNI_MUTED = 'rgba(255,255,255,0.45)';

interface Activity {
  id: string;
  type: 'lesson' | 'enrollment';
  title: string;
  subtitle: string;
  timestamp: string;
}

export const RecentActivity = () => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [activities, setActivities] = useState<Activity[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;

    async function fetchActivity() {
      try {
        // Fetch enrollments as activity
        const { data: enrollments, error } = await supabase
          .from('class_enrollments')
          .select(`
            last_active_at,
            classes (name)
          `)
          .eq('student_id', user!.id)
          .not('last_active_at', 'is', null)
          .order('last_active_at', { ascending: false })
          .limit(5);

        if (error) throw error;

        const mapped: Activity[] = (enrollments || []).map((e: any, i: number) => ({
          id: `act-${i}`,
          type: 'lesson',
          title: e.classes?.name || 'Unknown Class',
          subtitle: t('dashboard.activeIn', 'Studied lesson and updated progress'),
          timestamp: e.last_active_at
        }));

        setActivities(mapped);
      } catch (err) {
        console.error('Error fetching recent activity:', err);
      } finally {
        setLoading(false);
      }
    }

    fetchActivity();
  }, [user, t]);

  if (loading) return null;
  if (activities.length === 0) return null;

  return (
    <div style={{ marginTop: 32 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
        <Clock size={18} color={UNI_GOLD} />
        <h3 style={{ 
          fontSize: 14, 
          fontWeight: 700, 
          color: UNI_TEXT, 
          letterSpacing: '0.05em',
          textTransform: 'uppercase'
        }}>
          {t('dashboard.recentActivity', 'Recent Activity')}
        </h3>
      </div>

      <div style={{ 
        display: 'flex', 
        flexDirection: 'column', 
        gap: 12,
        background: UNI_CARD,
        border: `1px solid ${UNI_BORDER}`,
        borderRadius: 20,
        padding: '12px'
      }}>
        {activities.map((activity, i) => (
          <motion.div
            key={activity.id}
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: i * 0.1 }}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 16,
              padding: '12px 16px',
              borderRadius: 12,
              background: 'rgba(255,255,255,0.02)',
              border: '1px solid transparent',
              transition: 'all 0.2s'
            }}
          >
            <div style={{
              width: 36,
              height: 36,
              borderRadius: '50%',
              background: 'rgba(14,165,233,0.1)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#0ea5e9'
            }}>
              <BookOpen size={18} />
            </div>

            <div style={{ flex: 1 }}>
              <h4 style={{ margin: 0, fontSize: 13, fontWeight: 600, color: '#fff' }}>
                {activity.title}
              </h4>
              <p style={{ margin: 0, fontSize: 11, color: UNI_MUTED }}>
                {activity.subtitle}
              </p>
            </div>

            <span style={{ fontSize: 11, color: UNI_MUTED, fontWeight: 500 }}>
              {formatDistanceToNow(new Date(activity.timestamp), { addSuffix: true })}
            </span>
          </motion.div>
        ))}
      </div>
    </div>
  );
};
